import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Collapse,
  Button,
  Chip,
  Link,
  IconButton,
  Tooltip,
  Popover,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Select,
  MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { DataAsset } from '../../types/DataAsset';
import { policiesService } from '../../services/api';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import dataAssetService from '../../services/dataAssetService';

interface OverviewSectionProps {
  asset: DataAsset;
  onUpdate?: () => void;
}

interface InfoItem {
  label: string;
  value: string;
  color?: string;
}

/**
 * OverviewSection Component
 * Displays the overview/summary information for a data asset
 */
const OverviewSection: React.FC<OverviewSectionProps> = ({ asset, onUpdate }) => {
  const { settings } = useAccessibility();
  const [descriptionExpanded, setDescriptionExpanded] = useState(true);
  const [businessContextExpanded, setBusinessContextExpanded] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
  
  // Inline editing state
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({
    type: asset.type,
    domain: asset.domain,
    status: asset.status,
    certification: asset.certification,
    sensitivityLevel: asset.sensitivityLevel || ''
  });
  const [infoType, setInfoType] = useState<string>('');
  const [complianceDialogOpen, setComplianceDialogOpen] = useState(false);
  const [selectedCompliance, setSelectedCompliance] = useState<string[]>(asset.complianceFlags || []);
  const [policiesDialogOpen, setPoliciesDialogOpen] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState<string[]>([]);
  const [savingPolicies, setSavingPolicies] = useState(false);
  const [notification, setNotification] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Available policy options
  const availablePolicies = [
    'Customer Data Protection Policy',
    'PII Data Handling Standards',
    'Financial Data Retention Policy',
    'KYC Compliance Requirements',
    'GDPR Compliance Policy',
    'CCPA Privacy Policy',
    'Data Classification Policy',
    'Access Control Policy',
    'Data Encryption Policy',
    'Audit and Monitoring Policy',
    'Incident Response Policy',
    'Third-Party Data Sharing Policy'
  ];

  // Handle businessContext - it might be a Map or object
  const businessContextEntries = asset.businessContext
    ? Object.entries(asset.businessContext).filter(([key, value]) => 
        typeof value === 'string' && !key.match(/^\d+$/)
      )
    : [];

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>, type: string) => {
    event.stopPropagation();
    setInfoAnchorEl(event.currentTarget);
    setInfoType(type);
  };

  const handleFieldClick = (fieldName: string) => {
    setEditingField(fieldName);
  };

  const handleFieldChange = (fieldName: string, value: string) => {
    setEditValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleFieldSave = async (fieldName: string) => {
    try {
      const fieldValue = editValues[fieldName as keyof typeof editValues];
      console.log(`Saving ${fieldName}:`, fieldValue);
      console.log(`Asset ID:`, asset._id);
      
      // Prepare update data
      const updateData: Partial<DataAsset> = {
        [fieldName]: fieldValue
      };
      
      console.log('Update data:', updateData);
      
      // Call API to update
      const result = await dataAssetService.updateDataAsset(asset._id, updateData);
      console.log('Update result:', result);
      
      // Show success notification
      setNotification({
        open: true,
        message: `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} updated successfully`,
        severity: 'success'
      });
      
      setEditingField(null);
      
      // Refresh parent component if callback provided
      // This will re-fetch the asset data and update the UI
      if (onUpdate) {
        console.log('Calling onUpdate to refresh asset data');
        onUpdate();
      }
    } catch (error: any) {
      console.error(`Error saving ${fieldName}:`, error);
      console.error('Error details:', error.response?.data || error.message);
      
      const errorMessage = error.response?.data?.error || error.message || `Failed to update ${fieldName}`;
      
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      // Keep field in edit mode on error
    }
  };

  const handleFieldCancel = () => {
    setEditValues({
      type: asset.type,
      domain: asset.domain,
      status: asset.status,
      certification: asset.certification,
      sensitivityLevel: asset.sensitivityLevel || ''
    });
    setEditingField(null);
  };

  const handleFieldKeyDown = (e: React.KeyboardEvent, fieldName: string) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleFieldSave(fieldName);
    } else if (e.key === 'Escape') {
      handleFieldCancel();
    }
  };

  const handleInfoClose = () => {
    setInfoAnchorEl(null);
    setInfoType('');
  };

  const handleComplianceToggle = (framework: string) => {
    setSelectedCompliance(prev => 
      prev.includes(framework)
        ? prev.filter(f => f !== framework)
        : [...prev, framework]
    );
  };

  const handleComplianceSave = async () => {
    if (!asset._id) {
      setNotification({
        open: true,
        message: 'Asset ID is missing',
        severity: 'error'
      });
      return;
    }

    try {
      console.log('Updating compliance frameworks for asset:', asset._id);
      console.log('Selected frameworks:', selectedCompliance);
      
      const result = await dataAssetService.updateDataAsset(asset._id, {
        complianceFlags: selectedCompliance as ('GDPR' | 'CCPA' | 'PCI-DSS' | 'SOX' | 'HIPAA' | 'FERPA')[]
      });
      console.log('Update result:', result);
      
      setNotification({
        open: true,
        message: 'Compliance frameworks updated successfully!',
        severity: 'success'
      });
      
      setComplianceDialogOpen(false);
      
      // Refresh the asset data
      if (onUpdate) {
        console.log('Calling onUpdate to refresh asset data');
        onUpdate();
      }
    } catch (error: any) {
      console.error('Error updating compliance frameworks:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to update compliance frameworks',
        severity: 'error'
      });
    }
  };

  const handleComplianceCancel = () => {
    setSelectedCompliance(asset.complianceFlags || []);
    setComplianceDialogOpen(false);
  };

  const handlePoliciesOpen = () => {
    const currentPolicies = asset.governance?.policies || [];
    const policiesArray = currentPolicies.map(p => 
      typeof p === 'string' ? p : (p as any)?.name || ''
    );
    setSelectedPolicies(policiesArray);
    setPoliciesDialogOpen(true);
  };

  const handlePolicyToggle = (policy: string) => {
    setSelectedPolicies(prev => 
      prev.includes(policy)
        ? prev.filter(p => p !== policy)
        : [...prev, policy]
    );
  };

  const handlePoliciesSave = async () => {
    if (!asset._id) {
      setNotification({
        open: true,
        message: 'Asset ID is missing',
        severity: 'error'
      });
      return;
    }

    setSavingPolicies(true);
    try {
      console.log('Updating policies for asset:', asset._id);
      console.log('Selected policies:', selectedPolicies);
      
      const result = await policiesService.updatePolicies(asset._id, selectedPolicies);
      console.log('Update result:', result);
      
      setNotification({
        open: true,
        message: 'Policies updated successfully!',
        severity: 'success'
      });
      
      setPoliciesDialogOpen(false);
      
      // Reload the page to show updated data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('Error updating policies:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to update policies',
        severity: 'error'
      });
    } finally {
      setSavingPolicies(false);
    }
  };

  const handlePoliciesCancel = () => {
    setPoliciesDialogOpen(false);
  };

  const infoOpen = Boolean(infoAnchorEl);

  const getInfoContent = (): { title: string; content: string; items: InfoItem[] } | null => {
    switch (infoType) {
      case 'description':
        return {
          title: 'About Description',
          content: 'The description provides a comprehensive overview of this data asset, including its purpose, content, and how it should be used within your organization.',
          items: [
            { label: 'Purpose', value: 'Explains what this asset contains' },
            { label: 'Usage', value: 'How teams should utilize this data' },
            { label: 'Context', value: 'Business value and relevance' },
            { label: 'Last Updated', value: asset.lastModified ? new Date(asset.lastModified).toLocaleDateString() : 'N/A' }
          ]
        };
      case 'certification':
        return {
          title: 'About Certification',
          content: 'Certification levels indicate the quality, validation, and trustworthiness of this data asset.',
          items: [
            { label: 'None', value: 'No certification applied', color: '#9e9e9e' },
            { label: 'Pending', value: 'Under review for certification', color: '#ff9800' },
            { label: 'Bronze', value: 'Basic validation completed', color: '#cd7f32' },
            { label: 'Silver', value: 'Enhanced quality checks passed', color: '#c0c0c0' },
            { label: 'Gold', value: 'High-quality, fully validated', color: '#ffd700' },
            { label: 'Platinum', value: 'Premium tier, enterprise-grade', color: '#e5e4e2' },
            { label: 'Certified', value: 'Officially certified and approved', color: '#4caf50' }
          ]
        };
      case 'Status':
        return {
          title: 'About Status',
          content: 'The current lifecycle status of this data asset.',
          items: [
            { label: 'Development', value: 'Under development or testing' },
            { label: 'Production', value: 'Currently in production use' },
            { label: 'Archived', value: 'No longer actively used but preserved' },
            { label: 'Deprecated', value: 'Scheduled for retirement' }
          ]
        };
      case 'sensitivity':
        return {
          title: 'About Sensitivity Level',
          content: 'Indicates the level of data sensitivity and required protection measures.',
          items: [
            { label: 'Low', value: 'Public or non-sensitive data', color: '#4caf50' },
            { label: 'Medium', value: 'Internal use, moderate protection', color: '#ff9800' },
            { label: 'High', value: 'Confidential, restricted access', color: '#f44336' },
            { label: 'Critical', value: 'Highly sensitive, maximum security', color: '#d32f2f' }
          ]
        };
      case 'compliance':
        return {
          title: 'About Compliance Status',
          content: 'Indicates whether this asset meets regulatory and organizational compliance requirements.',
          items: [
            { label: 'Compliant', value: 'Meets all requirements', color: '#4caf50' },
            { label: 'Non-Compliant', value: 'Does not meet requirements', color: '#f44336' },
            { label: 'Unknown', value: 'Compliance status not determined', color: '#ff9800' },
            { label: 'Under Review', value: 'Currently being assessed', color: '#2196f3' }
          ]
        };
      case 'complianceFrameworks':
        return {
          title: 'About Compliance Frameworks',
          content: 'Regulatory frameworks and standards that this data asset must comply with.',
          items: [
            { label: 'GDPR', value: 'General Data Protection Regulation (EU)', color: '#1976d2' },
            { label: 'CCPA', value: 'California Consumer Privacy Act', color: '#1976d2' },
            { label: 'PCI-DSS', value: 'Payment Card Industry Data Security Standard', color: '#1976d2' },
            { label: 'SOX', value: 'Sarbanes-Oxley Act', color: '#1976d2' },
            { label: 'HIPAA', value: 'Health Insurance Portability and Accountability Act', color: '#1976d2' },
            { label: 'FERPA', value: 'Family Educational Rights and Privacy Act', color: '#1976d2' }
          ]
        };
      case 'privacy':
        return {
          title: 'About Privacy Classification',
          content: 'Defines the level of privacy protection required for this data asset based on its content and regulatory requirements.',
          items: [
            { label: 'Public', value: 'Publicly accessible information', color: '#4caf50' },
            { label: 'Internal', value: 'Internal use only, not for external sharing', color: '#ff9800' },
            { label: 'Confidential', value: 'Restricted access, sensitive business data', color: '#f57c00' },
            { label: 'Restricted', value: 'Highly restricted, requires special authorization', color: '#d32f2f' }
          ]
        };
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Description Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Description
            </Typography>
            <Tooltip title="Click for more information about this section">
              <IconButton
                size="small"
                onClick={(e) => handleInfoClick(e, 'description')}
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'rgba(0, 51, 102, 0.08)'
                  },
                  '&:focus': {
                    outline: '2px solid #003366',
                    outlineOffset: '2px'
                  }
                }}
                aria-label="More information about description"
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Button
            size="small"
            onClick={() => setDescriptionExpanded(!descriptionExpanded)}
            endIcon={descriptionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              textTransform: 'none',
              '&:focus': {
                outline: '2px solid #003366',
                outlineOffset: '2px'
              }
            }}
          >
            {descriptionExpanded ? 'Show Less' : 'Show More'}
          </Button>
        </Box>
        <Collapse in={descriptionExpanded}>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {asset.description || 'No description available.'}
          </Typography>
        </Collapse>
      </Paper>

      {/* Info Popover */}
      <Popover
        open={infoOpen}
        anchorEl={infoAnchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            maxWidth: 400,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid #e0e0e0'
          }
        }}
      >
        {getInfoContent() && (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {getInfoContent()!.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
              {getInfoContent()!.content}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', display: 'block', mb: 0.5 }}>
              {infoType === 'certification' || infoType === 'sensitivity' || infoType === 'compliance' ? 'Levels & Colors:' : 'Key Information:'}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
              {getInfoContent()!.items.map((item, index) => (
                <Typography key={index} component="li" variant="caption" color="text.secondary">
                  {item.color && (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: item.color,
                        mr: 0.5,
                        verticalAlign: 'middle',
                        border: '1px solid #ddd'
                      }}
                    />
                  )}
                  <strong>{item.label}:</strong> {item.value}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Popover>

      {/* Business Context Section */}
      {businessContextEntries.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Business Context
            </Typography>
            <Button
              size="small"
              onClick={() => setBusinessContextExpanded(!businessContextExpanded)}
              endIcon={businessContextExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {businessContextExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Box>
          <Collapse in={businessContextExpanded}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {businessContextEntries.map(([key, value]) => (
                <Link
                  key={key}
                  href={`#${value}`}
                  underline="hover"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '14px',
                    '&:hover': {
                      color: '#005a9c',
                    },
                  }}
                >
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </Link>
              ))}
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* Details Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Details
          </Typography>
          <Button
            size="small"
            onClick={() => setDetailsExpanded(!detailsExpanded)}
            endIcon={detailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ 
              color: 'primary.main',
              '&:hover': { bgcolor: 'rgba(0, 51, 102, 0.08)' }
            }}
          >
            {detailsExpanded ? 'Show Less' : 'Show More'}
          </Button>
        </Box>
        <Collapse in={detailsExpanded}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: settings.compactView ? { xs: '1fr', md: '1fr 1fr' } : '1fr',
            gap: settings.compactView ? 2 : 3,
            rowGap: settings.compactView ? 2.5 : 3
          }}>
            {/* Type - Read Only */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Type
              </Typography>
              <Typography variant="body2">{asset.type}</Typography>
            </Box>

            {/* Domain - Read Only */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Domain
              </Typography>
              <Typography variant="body2">{asset.domain}</Typography>
            </Box>

            {/* Status - Read Only */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Status
                </Typography>
                <Tooltip title="Click for more information">
                  <IconButton
                    size="small"
                    onClick={(e) => handleInfoClick(e, 'status')}
                    sx={{
                      p: 0.25,
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'rgba(0, 51, 102, 0.08)' }
                    }}
                    aria-label="More information about status"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Chip label={asset.status} size="small" />
            </Box>

            {/* Certification - Read Only */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Certification
                </Typography>
                <Tooltip title="Click to see certification levels and colors">
                  <IconButton
                    size="small"
                    onClick={(e) => handleInfoClick(e, 'certification')}
                    sx={{
                      p: 0.25,
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'rgba(0, 51, 102, 0.08)' }
                    }}
                    aria-label="More information about certification levels"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Chip
                label={asset.certification}
                size="small"
                color={asset.certification === 'certified' ? 'success' : 'default'}
              />
            </Box>

            {/* Stewards - Always visible */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Stewards
              </Typography>
              {asset.stewards && asset.stewards.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {asset.stewards.map((steward) => (
                    <Chip key={steward} label={steward} size="small" />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No stewards assigned
                </Typography>
              )}
            </Box>

            {/* Sensitivity Level - Read Only */}
            {asset.sensitivityLevel && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Sensitivity Level
                  </Typography>
                  <Tooltip title="Click to see sensitivity levels and colors">
                    <IconButton
                      size="small"
                      onClick={(e) => handleInfoClick(e, 'sensitivity')}
                      sx={{
                        p: 0.25,
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'rgba(0, 51, 102, 0.08)' }
                      }}
                      aria-label="More information about sensitivity levels"
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Chip
                  label={asset.sensitivityLevel}
                  size="small"
                  color={
                    asset.sensitivityLevel === 'Critical' || asset.sensitivityLevel === 'High'
                      ? 'error'
                      : asset.sensitivityLevel === 'Medium'
                      ? 'warning'
                      : 'success'
                  }
                />
              </Box>
            )}

            {/* Security Classification - Read Only */}
            {(asset as any).securityClassification && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Security
                </Typography>
                <Chip
                  label={(asset as any).securityClassification}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Governance Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
          Governance
        </Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {asset.governance?.complianceStatus && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Compliance Status
                  </Typography>
                  <Tooltip title="Click to see compliance status levels and colors">
                    <IconButton
                      size="small"
                      onClick={(e) => handleInfoClick(e, 'compliance')}
                      sx={{
                        p: 0.25,
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'rgba(0, 51, 102, 0.08)' }
                      }}
                      aria-label="More information about compliance status"
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Chip
                  label={asset.governance.complianceStatus}
                  size="small"
                  color={asset.governance.complianceStatus === 'Compliant' ? 'success' : 'warning'}
                  sx={{ ml: 0 }}
                />
              </Box>
            )}
            
            {/* Compliance Frameworks Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Compliance Frameworks
                  </Typography>
                  <Tooltip title="Click to learn about compliance frameworks" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => handleInfoClick(e, 'complianceFrameworks')}
                      sx={{
                        p: 0.5,
                        color: 'primary.main',
                        bgcolor: 'rgba(0, 51, 102, 0.04)',
                        '&:hover': { bgcolor: 'rgba(0, 51, 102, 0.12)' }
                      }}
                      aria-label="More information about compliance frameworks"
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {selectedCompliance.length > 0 ? (
                  selectedCompliance.map((framework, index) => (
                    <Chip 
                      key={index} 
                      label={framework} 
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No compliance frameworks selected
                  </Typography>
                )}
              </Box>
            </Box>
            
            {/* Policies Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Policies
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {asset.governance?.policies && asset.governance.policies.length > 0 ? (
                  asset.governance.policies.map((policy, index) => {
                    const policyLabel = typeof policy === 'string' 
                      ? policy 
                      : (policy as any)?.name || 'Policy';
                    return (
                      <Chip 
                        key={index} 
                        label={policyLabel} 
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    );
                  })
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No policies assigned
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

      {/* Compliance Frameworks Edit Dialog */}
      <Dialog
        open={complianceDialogOpen}
        onClose={handleComplianceCancel}
        maxWidth="sm"
        fullWidth
        aria-labelledby="compliance-dialog-title"
      >
        <DialogTitle
          id="compliance-dialog-title"
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            fontFamily: 'Merriweather, serif',
            fontWeight: 700,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>Edit Compliance Frameworks</span>
          <IconButton
            onClick={handleComplianceCancel}
            sx={{ color: 'white' }}
            aria-label="Close dialog"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select all regulatory frameworks and standards that this data asset must comply with.
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCompliance.includes('GDPR')}
                  onChange={() => handleComplianceToggle('GDPR')}
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    GDPR
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    General Data Protection Regulation (EU)
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCompliance.includes('CCPA')}
                  onChange={() => handleComplianceToggle('CCPA')}
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    CCPA
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    California Consumer Privacy Act
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCompliance.includes('PCI-DSS')}
                  onChange={() => handleComplianceToggle('PCI-DSS')}
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    PCI-DSS
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Payment Card Industry Data Security Standard
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCompliance.includes('SOX')}
                  onChange={() => handleComplianceToggle('SOX')}
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    SOX
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sarbanes-Oxley Act
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCompliance.includes('HIPAA')}
                  onChange={() => handleComplianceToggle('HIPAA')}
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    HIPAA
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Health Insurance Portability and Accountability Act
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCompliance.includes('FERPA')}
                  onChange={() => handleComplianceToggle('FERPA')}
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    FERPA
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Family Educational Rights and Privacy Act
                  </Typography>
                </Box>
              }
            />
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleComplianceCancel}
            variant="outlined"
            sx={{
              color: 'primary.main',
              borderColor: 'primary.main',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'rgba(0, 51, 102, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleComplianceSave}
            variant="contained"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#005a9c'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Policies Edit Dialog */}
      <Dialog
        open={policiesDialogOpen}
        onClose={handlePoliciesCancel}
        maxWidth="sm"
        fullWidth
        aria-labelledby="policies-dialog-title"
      >
        <DialogTitle
          id="policies-dialog-title"
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            fontFamily: 'Merriweather, serif',
            fontWeight: 700,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>Edit Policies</span>
          <IconButton
            onClick={handlePoliciesCancel}
            sx={{ color: 'white' }}
            aria-label="Close dialog"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the policies that apply to this data asset:
          </Typography>
          <FormGroup>
            {availablePolicies.map((policy) => (
              <FormControlLabel
                key={policy}
                control={
                  <Checkbox
                    checked={selectedPolicies.includes(policy)}
                    onChange={() => handlePolicyToggle(policy)}
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main'
                      }
                    }}
                  />
                }
                label={policy}
                sx={{ mb: 0.5 }}
              />
            ))}
          </FormGroup>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', fontStyle: 'italic' }}>
            {selectedPolicies.length} {selectedPolicies.length === 1 ? 'policy' : 'policies'} selected
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handlePoliciesCancel}
            variant="outlined"
            sx={{
              color: 'primary.main',
              borderColor: 'primary.main',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'rgba(0, 51, 102, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePoliciesSave}
            variant="contained"
            disabled={savingPolicies}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#005a9c'
              }
            }}
          >
            {savingPolicies ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OverviewSection;
